import { useCallback, useId, useMemo, useState } from "react";
import type React from "react";

const defaultFormatCreateLabel = (v: string) => `Create "${v}"`;

import { buildAriaDescribedBy, buildTestIdPrefix } from "src/utils/fieldIds";
import {
  useCreatableItems,
  CREATE_PREFIX,
  isCreateValue,
} from "./useCreatableItems";
import { useMultiSelectOptions } from "./useMultiSelectOptions";
import { SIZE_CONFIG } from "src/components/Select/constants";
import type { MultiSelectProps } from "src/components/Select/types";

export function useMultiSelectState(props: MultiSelectProps) {
  const {
    id: idProp,
    size = "medium",
    label = "",
    error = "",
    helpText = "",
    value: valueProp,
    defaultValue,
    onChange,
    dataTestId,
    isCreatable = false,
    onCreateOption,
    formatCreateLabel = defaultFormatCreateLabel,
    formatOptionLabel,
    children,
  } = props;

  const opt = useMultiSelectOptions(props);

  const generatedId = useId();
  const id = idProp ?? generatedId;
  const errorId = `error_${id}`;
  const helpTextId = `helpText_${id}`;
  const sizeConfig = SIZE_CONFIG[size];

  const isControlled = valueProp !== undefined;
  const [internalValue, setInternalValue] = useState<string[]>(
    defaultValue ?? []
  );
  const selectedValues = isControlled ? valueProp : internalValue;

  const handleChange = useCallback(
    (values: string[]) => {
      setInternalValue(values);
      onChange?.(values);
    },
    [onChange]
  );

  const {
    showCreateOption,
    effectiveItems,
    creatableFilter,
    handleInputValueChange,
    handleValueChange,
  } = useCreatableItems({
    isCreatable,
    isGrouped: opt.isGrouped,
    flatOptions: opt.flatOptions,
    selectedValues,
    baseUiItems: opt.baseUiItems,
    onCreateOption,
    onChange: handleChange,
    onAsyncInputChange: opt.isAsync ? opt.handleAsyncInputChange : undefined,
    children,
  });

  const ariaDescribedBy = buildAriaDescribedBy(
    error,
    errorId,
    helpText,
    helpTextId
  );
  const testIdPrefix = buildTestIdPrefix(dataTestId, label);

  const flatOptionsMap = useMemo(
    () => new Map(opt.flatOptions.map(o => [o.value, o])),
    [opt.flatOptions]
  );

  const labelForValue = useCallback(
    (val: string) => {
      if (isCreateValue(val)) {
        return formatCreateLabel(val.slice(CREATE_PREFIX.length));
      }
      return flatOptionsMap.get(val)?.label ?? val ?? "";
    },
    [flatOptionsMap, formatCreateLabel]
  );

  const renderOption = useCallback<(val: string) => React.ReactNode>(
    val => {
      if (formatOptionLabel && !isCreateValue(val)) {
        const option = flatOptionsMap.get(val);
        if (option) return formatOptionLabel(option);
      }
      return labelForValue(val);
    },
    [formatOptionLabel, flatOptionsMap, labelForValue]
  );

  return {
    ...opt,
    id,
    errorId,
    helpTextId,
    sizeConfig,
    isControlled,
    selectedValues,
    effectiveItems,
    creatableFilter,
    handleInputValueChange,
    handleValueChange,
    showCreateOption,
    ariaDescribedBy,
    testIdPrefix,
    labelForValue,
    renderOption,
    flatOptionsMap,
    label,
    error,
    helpText,
  };
}
